import { DEFAULT_STOCKPILE_TOOL_MODE, stockpileToolModeAtom } from '@/atoms/stockpile.atom';
import { addSurfaceModeAtom, DEFAULT_ADD_SURFACE_MODE } from '@/atoms/viewer.atom';
import { execute } from '@/graphql/execute';
import { useProjectHotkeys } from '@/hooks/useAppHotkeys';
import { useClipboxExportProgress } from '@/hooks/useClipboxExportProgress';
import { useVectorExportProgress } from '@/hooks/useVectorExportProgress';
import { useReportProgress } from '@/hooks/useReportProgress';
import { useDeviationProgress } from '@/hooks/useDeviationProgress';
import { useGroundClassifyProgress } from '@/hooks/useGroundClassifyProgress';
import { useLandxmlExportProgress } from '@/hooks/useLandxmlExportProgress';
import { useMeshExportProgress } from '@/hooks/useMeshExportProgress';
import { useRainfallProgress } from '@/hooks/useRainfallProgress';
import { getProjectQuery } from '@/queries/project.query';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { FunctionComponent, useEffect } from 'react';
import { Outlet, useParams } from 'react-router';

interface iProjectWrapperProps {}

const ProjectWrapper: FunctionComponent<iProjectWrapperProps> = () => {
  const { projectId } = useParams();
  if (!projectId) throw new Error('Project ID is required');

  const { data, isLoading, error } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId }),
  });

  const project = data?.project;
  const href = `/projects/${projectId}`;

  // Defensive reset: AddSurface and Stockpile tool atoms are global (not
  // scoped per project). If the user starts picking in project A and the
  // route param flips to project B (sidebar click, browser back, deep link),
  // the in-progress geometry would otherwise get saved against the wrong
  // project. Wipe both atoms whenever projectId changes.
  const setAddSurfaceMode = useSetAtom(addSurfaceModeAtom);
  const setStockpileToolMode = useSetAtom(stockpileToolModeAtom);
  useEffect(() => {
    setAddSurfaceMode(DEFAULT_ADD_SURFACE_MODE);
    setStockpileToolMode(DEFAULT_STOCKPILE_TOOL_MODE);
  }, [projectId, setAddSurfaceMode, setStockpileToolMode]);

  // Mount the deviation progress hook on the project shell — this is the
  // one component that's always alive while the user is anywhere within
  // /projects/<id>/* (viewer, analyses, attribute table, etc.). Mounting
  // it on ViewerPage alone meant Run-Deviation triggered from the Layers
  // panel of the AttributeTable produced a tracker that nothing was
  // subscribed to, so polling never started. The badge consumer
  // (ViewerPage) reads `runningCount` off the outlet context.
  const { runningCount: deviationRunningCount } = useDeviationProgress(projectId);
  // Same pattern as the deviation hook — keeps the loading toast open until
  // the new derived layers actually appear in the layer panel, instead of
  // dismissing it the moment the GraphQL mutation returns the batch ID.
  useGroundClassifyProgress(projectId);
  // Same pattern again, but resolves on parent job PROCESSED (no derived
  // layer is created — the result is a download-only byproduct).
  useClipboxExportProgress(projectId);
  // Same pattern — resolves on VECTOR_EXPORT job PROCESSED. No derived layer;
  // the result is a download-only SHP/DXF/DWG/GPKG byproduct.
  useVectorExportProgress(projectId);
  // Same pattern — resolves on LANDXML_EXPORT job PROCESSED; refreshes the
  // layer list (a MODEL/LANDXML layer is created) and offers a download.
  useLandxmlExportProgress(projectId);
  // Same pattern — resolves on PDF_REPORT job PROCESSED. No derived layer;
  // the result is a download-only PDF byproduct.
  useReportProgress(projectId);
  // Same pattern — resolves on MESH job PROCESSED. No derived layer;
  // the result is a download-only mesh byproduct.
  useMeshExportProgress(projectId);
  // Resolves when the GROUP/TILE/VECTOR rainfall layer triple appears
  // in the layer panel (postprocess inserts all three with derivedFrom
  // set on each).
  useRainfallProgress(projectId);

  // Register project-scoped hotkeys (enabled is controlled inside the hook via useParams)
  useProjectHotkeys();
  return (
    <Outlet
      context={{
        project,
        projectLoading: isLoading,
        projectError: error,
        href,
        deviationRunningCount,
      }}
    />
  );
};

export default ProjectWrapper;
